import { defaultStackParser, init, makeFetchTransport, withScope } from '@sentry/browser';
import { setUser, reactRouterV6BrowserTracingIntegration } from '@sentry/react';
import { ScopeContext, User } from '@sentry/types';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

import { AuthResources } from '_lib/featureComponents/auth/authContext';

import {
  BadRequestError,
  BaseHttpError,
  HttpConnectionError,
  UnauthorizedError,
  UnsupportedAppVersionError
} from '../api/apiErrors';
import { API_URL, APP_NAME, DEBUG, ENV_NAME, SENTRY_DSN, SENTRY_ENABLED, VERSION } from '../global/config';

function reactBrowserTracingIntegration() {
  return reactRouterV6BrowserTracingIntegration({
    useEffect,
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes
  });
}

/**
 * Initializes sentry for the entire application. This should only be done once per application.
 * For example, when running our apps with SPA, we do NOT need to use this since SPA inits sentry on its own.
 */
export function initSentry() {
  init({
    debug: DEBUG,
    dsn: SENTRY_DSN,
    enabled: SENTRY_ENABLED,
    environment: ENV_NAME,
    integrations: (defaultIntegrations) => [...defaultIntegrations, reactBrowserTracingIntegration()],
    normalizeDepth: 10,
    release: `${APP_NAME}-${VERSION}`,
    tracesSampleRate: 0.5,
    tracePropagationTargets: [API_URL.replace('/api', ''), /^\//],
    transport: makeFetchTransport,
    stackParser: defaultStackParser,
    beforeSend: (event, hint) => {
      if (!SENTRY_ENABLED) {
        console.log('Error caught, but sentry is disabled. Here is the full event:', event, hint);
        return null;
      }
      return event;
    }
  });
  AuthResources.authUserCacheResource.get().then((user) => {
    setUser(user);
  });
}

/**
 * Captures Exception with the information of the logged in user.
 *
 * Discards errors with status 401, 400, -1. (NOTE - should be handled elsewhere!)
 *
 * @param err
 * @param reportingCtx
 * @returns
 */
export const reportException = (err: unknown, reportingCtx?: Partial<ScopeContext>) => {
  if (
    err instanceof BadRequestError ||
    err instanceof UnauthorizedError ||
    err instanceof UnsupportedAppVersionError ||
    err instanceof HttpConnectionError
  )
    return;

  withScope((scope) => {
    if (reportingCtx?.user) scope.setUser(reportingCtx?.user);
    if (reportingCtx?.extra) scope.setExtras(reportingCtx.extra);
    if (reportingCtx?.level) scope.setLevel(reportingCtx.level);
    if (reportingCtx?.tags) scope.setTags(reportingCtx.tags);
    if (reportingCtx?.fingerprint) scope.setFingerprint(reportingCtx.fingerprint);
    if (reportingCtx?.contexts) {
      Object.entries(reportingCtx.contexts).forEach(([ctxKey, ctx]) => scope.setContext(ctxKey, ctx || null));
    }
    if (reportingCtx?.requestSession) scope.setRequestSession(reportingCtx.requestSession);

    if (err instanceof BaseHttpError) {
      const context = err.getContext();
      scope.setContext('Request Information', { ...context });
      scope.setTag('url', context.requestUrl);
    }

    scope.captureException(err);
  });
};

/**
 * Captures Message with the information of the logged in user
 * @param message
 * @param reportingCtx
 */
export const reportMessage = (message: string, reportingCtx?: Partial<ScopeContext>) => {
  withScope((scope) => {
    if (reportingCtx?.user) scope.setUser(reportingCtx?.user);
    if (reportingCtx?.extra) scope.setExtras(reportingCtx.extra);
    if (reportingCtx?.level) scope.setLevel(reportingCtx.level);
    if (reportingCtx?.tags) scope.setTags(reportingCtx.tags);
    if (reportingCtx?.fingerprint) scope.setFingerprint(reportingCtx.fingerprint);
    if (reportingCtx?.contexts) {
      Object.entries(reportingCtx.contexts).forEach(([ctxKey, ctx]) => scope.setContext(ctxKey, ctx || null));
    }
    if (reportingCtx?.requestSession) scope.setRequestSession(reportingCtx.requestSession);

    scope.captureMessage(message);
  });
};

export function setReportingUser(user: User | null) {
  setUser(user);
}
